import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { deneignementService } from '@/services/deneignementService';
import { getErrorMessage } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { useRegulationMercure, type RegulationEvent } from '@/hooks/useMercure';
import {
  regulationService,
  type TimelineIntervention,
  type TimelineEquipe,
  type TimelineEquipeAffectation,
  type Conflit,
  type Suggestion,
  type GeneratePlanResponse,
  type PlanAffectation,
} from '@/services/regulationService';

// Type pour les donnees de drag and drop (reassignation entre equipes)
interface DragData {
  interventionId: number;
  zone: number;
  sourceEquipeId: number;
  parking: string;
  numeroOrdre: string;
}

// Type pour le drag libre d'un bloc deja planifie dans la Timeline: un seul geste
// (maintenir clic + glisser) permet a la fois de changer l'heure (deplacement
// horizontal) et l'equipe (deplacement vers une autre ligne), au lieu de deux
// mecanismes distincts (drag HTML5 natif pour l'equipe, drag souris pour l'heure).
interface FreeDragData {
  interventionId: number;
  zone: number;
  sourceEquipeId: number;
  parking: string;
  numeroOrdre: string;
  startX: number;
  originalHeure: string;
  currentHeure: string;
  currentEquipeId: number;
  isActive: boolean;
}
import { StatusBadge } from '@/components/common/StatusBadge';
import { PrioriteBadge } from '@/components/common/PrioriteBadge';
import { NowLine } from '@/components/regulation/NowLine';
import { EditAffectationModal } from '@/components/regulation/EditAffectationModal';
import { STATUT_TIMELINE_COLORS } from '@/components/regulation/statutColors';
import {
  CalendarDaysIcon,
  ArrowPathIcon,
  BoltIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  UserGroupIcon,
  ClockIcon,
  EyeIcon,
  ArrowsRightLeftIcon,
  CheckIcon,
  DocumentArrowDownIcon,
  ClipboardDocumentListIcon,
  Bars2Icon,
  ArrowsPointingOutIcon,
} from '@heroicons/react/24/outline';

// Couleurs pour les niveaux de conflit
const CONFLIT_COLORS = {
  critique: 'bg-red-100 border-red-300 text-red-800',
  warning: 'bg-amber-100 border-amber-300 text-amber-800',
  info: 'bg-blue-100 border-blue-300 text-blue-800',
};

const CONFLIT_ICONS = {
  critique: ExclamationTriangleIcon,
  warning: ExclamationTriangleIcon,
  info: InformationCircleIcon,
};

// Couleurs pour les statuts dans la timeline

/**
 * Page de regulation des interventions de deneignement
 * SFD v2.1: Vue timeline, detection conflits, optimisation
 */
export function RegulationPage() {
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [editAffectation, setEditAffectation] = useState<{
    interventionId: number;
    numeroOrdre: string;
    parking: string;
    zone: number;
    statut: string;
    currentEquipeId: number;
    heurePlanifiee: string;
    heureSouhaitee: string;
  } | null>(null);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [generatedPlan, setGeneratedPlan] = useState<GeneratePlanResponse | null>(null);
  const [reassignData, setReassignData] = useState<{
    interventionId: number;
    zone: number;
    equipeId: number;
  } | null>(null);

  // Etat pour le drag and drop (reassignation entre equipes)
  const [draggedItem, setDraggedItem] = useState<DragData | null>(null);
  const [dropTargetEquipe, setDropTargetEquipe] = useState<number | null>(null);

  // Etat pour le drag libre d'un bloc de la Timeline (heure + equipe en un seul geste)
  const [freeDrag, setFreeDrag] = useState<FreeDragData | null>(null);
  // Ref (plutot qu'un state) pour savoir si le pointeur a reellement bouge pendant le
  // geste: un state serait sujet a une course avec l'evenement natif "click" qui suit
  // le mouseup (son re-rendu n'est pas garanti termine avant que le click se declenche),
  // alors qu'une ref est lue de maniere synchrone et fiable par le onClick du bloc.
  const justDraggedRef = useRef(false);

  // Etat pour l'auto-refresh
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  // Abonnement aux mises a jour temps reel via Mercure
  const handleMercureEvent = useCallback(
    (event: RegulationEvent) => {
      console.log('[Mercure] Evenement recu:', event.type);

      // Invalider les queries en fonction du type d'evenement
      switch (event.type) {
        case 'intervention_created':
        case 'intervention_updated':
        case 'statut_change':
        case 'affectation_created':
        case 'affectation_updated':
        case 'plan_applied':
        case 'timeline_refresh':
          // Rafraichir la timeline et les conflits
          queryClient.invalidateQueries({ queryKey: ['regulation-timeline'] });
          queryClient.invalidateQueries({ queryKey: ['regulation-conflicts'] });
          queryClient.invalidateQueries({ queryKey: ['regulation-optimization'] });
          break;

        case 'charge_update':
        // SFD jalonnement Contact/Large (Lot 5 - #125) : la progression
        // "x / N jalons" doit se mettre a jour en temps reel (< 2s) apres
        // chaque tap agent. MercureService::publishInterventionUpdate()
        // publie sur ce topic avec le prefixe 'intervention_' devant
        // l'eventType transmis par les controllers (jalon_debut, jalon_fin,
        // jalon_corrige, jalon_incident, configuration_maj,
        // immatriculation_maj) - absents jusqu'ici de ce switch, la
        // Timeline ne se rafraichissait donc jamais sur ces evenements.
        case 'intervention_jalon_debut':
        case 'intervention_jalon_fin':
        case 'intervention_jalon_corrige':
        case 'intervention_jalon_incident':
        case 'intervention_configuration_maj':
        case 'intervention_immatriculation_maj':
          // Rafraichir uniquement la timeline (charge des equipes / progression)
          queryClient.invalidateQueries({ queryKey: ['regulation-timeline'] });
          break;

        case 'conflit_detected':
        case 'retard_detected':
          // Rafraichir les conflits
          queryClient.invalidateQueries({ queryKey: ['regulation-conflicts'] });
          break;
      }
    },
    [queryClient]
  );

  useRegulationMercure(handleMercureEvent);

  // Chargement des donnees
  const { data: timeline, isLoading: isLoadingTimeline, refetch: refetchTimeline } = useQuery({
    queryKey: ['regulation-timeline', selectedDate],
    queryFn: () => regulationService.getTimeline(selectedDate),
    refetchInterval: autoRefreshEnabled ? 15000 : false, // Rafraichissement toutes les 15 secondes si actif
  });

  const { data: conflicts, isLoading: isLoadingConflicts, refetch: refetchConflicts } = useQuery({
    queryKey: ['regulation-conflicts', selectedDate],
    queryFn: () => regulationService.getConflicts(selectedDate),
    refetchInterval: autoRefreshEnabled ? 15000 : false,
  });

  // Effet pour mettre a jour l'horodatage du dernier refresh
  useEffect(() => {
    if (timeline) {
      setLastRefresh(new Date());
    }
  }, [timeline]);

  // Gestion globale des evenements souris pour le drag libre (heure + equipe)
  useEffect(() => {
    if (!freeDrag?.isActive) return;

    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (!freeDrag?.isActive) return;
      // Trouver la ligne d'equipe sous le curseur pour calculer la position
      // horaire dans cette ligne et detecter un eventuel changement d'equipe
      const timelineContainers = document.querySelectorAll('[data-timeline-container]');
      timelineContainers.forEach((container) => {
        const rect = container.getBoundingClientRect();
        if (e.clientY >= rect.top && e.clientY <= rect.bottom) {
          const deltaX = e.clientX - freeDrag.startX;
          const deltaPercent = (deltaX / rect.width) * 100;
          const originalPosition = getInterventionPositionGlobal(freeDrag.originalHeure);
          const newPosition = Math.max(0, Math.min(100, originalPosition + deltaPercent));
          const newHeure = positionToHeureGlobal(newPosition);
          const equipeIdAttr = container.getAttribute('data-equipe-id');
          const hoveredEquipeId = equipeIdAttr ? Number(equipeIdAttr) : freeDrag.currentEquipeId;
          if (newHeure !== freeDrag.originalHeure || hoveredEquipeId !== freeDrag.sourceEquipeId) {
            justDraggedRef.current = true;
          }
          setFreeDrag(prev => prev ? { ...prev, currentHeure: newHeure, currentEquipeId: hoveredEquipeId } : null);
          setDropTargetEquipe(hoveredEquipeId);
        }
      });
    };

    const handleGlobalMouseUp = () => {
      if (!freeDrag?.isActive) return;

      if (freeDrag.currentEquipeId !== freeDrag.sourceEquipeId) {
        handleDrop(freeDrag.currentEquipeId);
      } else {
        setDraggedItem(null);
        setDropTargetEquipe(null);
      }

      if (freeDrag.currentHeure !== freeDrag.originalHeure) {
        updateScheduleMutation.mutate({
          intervention_id: freeDrag.interventionId,
          heure_planifiee: freeDrag.currentHeure,
        });
      }

      setFreeDrag(null);
    };

    document.addEventListener('mousemove', handleGlobalMouseMove);
    document.addEventListener('mouseup', handleGlobalMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [freeDrag]);

  const { data: optimization } = useQuery({
    queryKey: ['regulation-optimization', selectedDate],
    queryFn: () => regulationService.getOptimization(selectedDate),
    enabled: !!timeline,
  });

  // Mutations
  const reassignMutation = useMutation({
    mutationFn: regulationService.reassign,
    onSuccess: () => {
      // Forcer le rafraichissement immediat de toutes les donnees
      queryClient.invalidateQueries({ queryKey: ['regulation-timeline'] });
      queryClient.invalidateQueries({ queryKey: ['regulation-conflicts'] });
      queryClient.invalidateQueries({ queryKey: ['regulation-optimization'] });
      setShowReassignModal(false);
      setReassignData(null);
      setLastRefresh(new Date());
    },
  });

  const applySuggestionsMutation = useMutation({
    mutationFn: () => regulationService.applySuggestions({ date: selectedDate }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['regulation-timeline'] });
      queryClient.invalidateQueries({ queryKey: ['regulation-conflicts'] });
      queryClient.invalidateQueries({ queryKey: ['regulation-optimization'] });
    },
  });

  // Mutation pour generer un plan optimise
  const generatePlanMutation = useMutation({
    mutationFn: () => regulationService.generatePlan(selectedDate),
    onSuccess: (data) => {
      setGeneratedPlan(data);
      setShowPlanModal(true);
    },
  });

  // Mutation pour appliquer le plan
  const applyPlanMutation = useMutation({
    mutationFn: (affectations: PlanAffectation[]) =>
      regulationService.applyPlan({ affectations }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['regulation-timeline'] });
      queryClient.invalidateQueries({ queryKey: ['regulation-conflicts'] });
      queryClient.invalidateQueries({ queryKey: ['regulation-optimization'] });
      setShowPlanModal(false);
      setGeneratedPlan(null);
    },
  });

  // Mutation pour modifier l'heure d'une intervention
  const updateScheduleMutation = useMutation({
    mutationFn: regulationService.updateSchedule,
    onSuccess: (data) => {
      // Forcer le rafraichissement immediat
      queryClient.invalidateQueries({ queryKey: ['regulation-timeline'] });
      queryClient.invalidateQueries({ queryKey: ['regulation-conflicts'] });
      queryClient.invalidateQueries({ queryKey: ['regulation-optimization'] });
      toast.success(data.message);
      setLastRefresh(new Date());
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Erreur lors de la modification');
    },
  });

  // Fonctions utilitaires pour le calcul de position (definies en dehors des callbacks)
  // Journee complete 00h-24h (une intervention de deneigement peut etre
  // planifiee de nuit, cf operations hivernales hors heures ouvrees) -
  // anciennement limite a 6h-22h, ce qui masquait toute planification
  // nocturne sur la timeline.
  const getInterventionPositionGlobal = (heure: string) => {
    const [h, m] = heure.split(':').map(Number);
    const minutes = h * 60 + m;
    const totalMinutes = 24 * 60;
    return (minutes / totalMinutes) * 100;
  };

  const positionToHeureGlobal = (position: number): string => {
    const totalMinutes = 24 * 60;
    // Arrondi sur le total de minutes (pas minutes%60 isolement) pour eviter
    // un report a 60 min non reporte sur l'heure (bug preexistant : 21:56
    // arrondissait a "21:60" affiche "21:00" au lieu de "22:00").
    let minutes = Math.round(((position / 100) * totalMinutes) / 15) * 15;
    minutes = Math.max(0, Math.min(totalMinutes - 15, minutes));
    const heures = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${String(heures).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  };

  // Navigation de date
  const goToPreviousDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() - 1);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const goToNextDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + 1);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const goToToday = () => {
    setSelectedDate(new Date().toISOString().split('T')[0]);
  };

  // Refresh manuel
  const handleRefresh = () => {
    refetchTimeline();
    refetchConflicts();
    queryClient.invalidateQueries({ queryKey: ['regulation-optimization'] });
    setLastRefresh(new Date());
  };

  // Plages horaires pour la timeline (6h - 22h)
  const plagesHoraires = useMemo(() => {
    return timeline?.plages_horaires || Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00`);
  }, [timeline]);

  // Interventions pas encore affectees a une equipe : c'est le travail du jour pour
  // le regulateur, mais elles n'apparaissent dans aucune ligne de la Timeline par
  // equipe (organisee par equipe, donc sans ligne pour une intervention sans equipe) -
  // seule la table generale "Interventions du jour", positionnee sous tout le Gantt,
  // les listait, noyees parmi toutes les autres. Mise en avant dans une section dediee.
  const interventionsAPlanifier = useMemo(() => {
    return (timeline?.interventions || []).filter((i) => i.statut === 'en_attente');
  }, [timeline]);

  // Handlers pour le drag and drop
  const handleDragStart = useCallback((data: DragData) => {
    setDraggedItem(data);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedItem(null);
    setDropTargetEquipe(null);
  }, []);

  const handleDragOver = useCallback((equipeId: number) => {
    setDropTargetEquipe(equipeId);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDropTargetEquipe(null);
  }, []);

  const handleDrop = useCallback((targetEquipeId: number) => {
    if (draggedItem && draggedItem.sourceEquipeId !== targetEquipeId) {
      const targetEquipe = timeline?.equipes.find(e => e.id === targetEquipeId);
      if (targetEquipe) {
        // Interventions jalonnement Contact/Large (#126) : pas de notion de
        // zone numerotee ni de contrainte pony/tracteur par zone - ce
        // controle n'existe que pour le modele historique. `draggedItem.zone`
        // y est de toute facon une valeur par defaut sans signification
        // (timeline.equipes des interventions jalonnement n'a pas de zone
        // reelle, cf RegulationService::formatInterventionForTimeline()).
        const intervention = timeline?.interventions.find(i => i.id === draggedItem.interventionId);
        const estJalonnement = intervention?.jalons_attendus != null;

        const requiresPony = draggedItem.zone <= 4;
        const isPonyEquipe = targetEquipe.type_engin === 'pony';
        const compatible = estJalonnement || (requiresPony && isPonyEquipe) || (!requiresPony && !isPonyEquipe);

        if (compatible) {
          toast.loading(`Reassignation de ${draggedItem.numeroOrdre} vers ${targetEquipe.nom}...`, { id: 'reassign' });
          reassignMutation.mutate({
            intervention_id: draggedItem.interventionId,
            zone: draggedItem.zone,
            equipe_id: targetEquipeId,
          }, {
            onSuccess: () => {
              toast.success(`${draggedItem.numeroOrdre} reassignee a ${targetEquipe.nom}`, { id: 'reassign' });
            },
            onError: (error) => {
              toast.error(getErrorMessage(error), { id: 'reassign' });
            },
          });
        } else {
          // Afficher un message d'erreur
          toast.error(
            `Incompatibilite: Zone ${draggedItem.zone} requiert un ${requiresPony ? 'Pony' : 'Tracteur'}, ` +
            `l'equipe ${targetEquipe.nom} dispose d'un ${isPonyEquipe ? 'Pony' : 'Tracteur'}`
          );
        }
      }
    }
    setDraggedItem(null);
    setDropTargetEquipe(null);
  }, [draggedItem, timeline?.equipes, timeline?.interventions, reassignMutation]);


  // Handler pour le debut du drag libre d'un bloc deja planifie (utilise les
  // events globaux): demarre a la fois le suivi d'heure et d'equipe.
  const handleFreeDragStart = useCallback((data: DragData, startX: number, heure: string) => {
    justDraggedRef.current = false;
    setDraggedItem(data);
    setFreeDrag({
      interventionId: data.interventionId,
      zone: data.zone,
      sourceEquipeId: data.sourceEquipeId,
      parking: data.parking,
      numeroOrdre: data.numeroOrdre,
      startX,
      originalHeure: heure,
      currentHeure: heure,
      currentEquipeId: data.sourceEquipeId,
      isActive: true,
    });
  }, []);

  if (isLoadingTimeline && isLoadingConflicts) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
        <p className="text-sm text-gray-500 mt-3">Chargement de la regulation...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <h2 className="text-2xl font-bold text-gray-900">Regulation</h2>
          {/* Indicateur temps reel */}
          <div className="flex items-center space-x-1.5 px-2 py-1 bg-green-50 border border-green-200 rounded-full">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
            </span>
            <span className="text-xs text-green-700 font-medium">Temps reel</span>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {/* Toggle auto-refresh */}
          <div className="flex items-center space-x-2 px-3 py-1.5 bg-gray-100 rounded-lg">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={autoRefreshEnabled}
                onChange={(e) => setAutoRefreshEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="relative w-9 h-5 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-500"></div>
              <span className="ms-2 text-xs text-gray-600">Auto</span>
            </label>
            <span className="text-xs text-gray-400">
              {lastRefresh.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          </div>
          <button onClick={handleRefresh} className="btn-secondary">
            <ArrowPathIcon className={`w-5 h-5 mr-2 ${isLoadingTimeline ? 'animate-spin' : ''}`} />
            Rafraichir
          </button>
          <button
            onClick={() => generatePlanMutation.mutate()}
            disabled={generatePlanMutation.isPending}
            className="btn-primary"
          >
            <BoltIcon className="w-5 h-5 mr-2" />
            {generatePlanMutation.isPending ? 'Generation...' : 'Generer plan optimise'}
          </button>
        </div>
      </div>

      {/* Selecteur de date */}
      <div className="card">
        <div className="card-body py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button onClick={goToPreviousDay} className="p-2 hover:bg-gray-100 rounded">
                <ChevronLeftIcon className="w-5 h-5" />
              </button>
              <div className="flex items-center space-x-2">
                <CalendarDaysIcon className="w-5 h-5 text-gray-400" />
                <input
                  type="date"
                  className="input w-40"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                />
              </div>
              <button onClick={goToNextDay} className="p-2 hover:bg-gray-100 rounded">
                <ChevronRightIcon className="w-5 h-5" />
              </button>
              <button onClick={goToToday} className="btn-secondary text-sm">
                Aujourd'hui
              </button>
            </div>

            {/* Statistiques rapides */}
            {timeline?.statistiques && (
              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center space-x-2">
                  <span className="text-gray-500">Total:</span>
                  <span className="font-semibold">{timeline.statistiques.total}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="w-2 h-2 rounded-full bg-blue-500" />
                  <span>En attente: {timeline.statistiques.en_attente}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="w-2 h-2 rounded-full bg-red-500" />
                  <span>En cours: {timeline.statistiques.en_cours}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="w-2 h-2 rounded-full bg-green-500" />
                  <span>Terminees: {timeline.statistiques.terminees}</span>
                </div>
                {timeline.statistiques.en_retard > 0 && (
                  <div className="flex items-center space-x-2 text-red-600">
                    <ExclamationTriangleIcon className="w-4 h-4" />
                    <span>En retard: {timeline.statistiques.en_retard}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Interventions a planifier - mises en avant car c'est l'ecran principal de la
          regulation : une intervention sans equipe n'a aucune ligne dans la Timeline
          par equipe ci-dessous, elle y serait donc invisible */}
      <div className={`card border-l-4 ${interventionsAPlanifier.length > 0 ? 'border-l-orange-500' : 'border-l-green-500'}`}>
        <div className="card-header flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center">
            <ClipboardDocumentListIcon className={`w-5 h-5 mr-2 ${interventionsAPlanifier.length > 0 ? 'text-orange-500' : 'text-green-500'}`} />
            A planifier ({interventionsAPlanifier.length})
          </h3>
        </div>
        <div className="card-body p-0">
          {interventionsAPlanifier.length === 0 ? (
            <div className="p-4 text-center text-gray-500 flex items-center justify-center gap-2">
              <CheckCircleIcon className="w-5 h-5 text-green-500" />
              Toutes les interventions du jour sont affectees a une equipe
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>N° Ordre</th>
                    <th>H. Souhaitee</th>
                    <th>H. Planifiee</th>
                    <th>Parking</th>
                    <th>Priorite</th>
                    <th>Statut</th>
                    <th>Progression</th>
                    <th>Equipes</th>
                    <th>Ecart</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {interventionsAPlanifier.map((intervention) => (
                    <InterventionRow
                      key={intervention.id}
                      intervention={intervention}
                      onReassign={(zone) => {
                        setReassignData({
                          interventionId: intervention.id,
                          zone,
                          equipeId: 0,
                        });
                        setShowReassignModal(true);
                      }}
                      onDragStart={handleDragStart}
                      onDragEnd={handleDragEnd}
                      isBeingDragged={draggedItem?.interventionId === intervention.id}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Contenu principal: Timeline + Conflits */}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
        {/* Timeline (4/5): zone la plus utile au quotidien, privilegiee sur Conflits/Suggestions */}
        <div className="xl:col-span-4 space-y-4">
          {/* Timeline par equipe */}
          <div className="card">
            <div className="card-header flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center">
                <UserGroupIcon className="w-5 h-5 mr-2" />
                Timeline par equipe
              </h3>
              <Link
                to="/regulation/timeline"
                className="text-xs text-primary-600 hover:underline flex items-center gap-1"
                title="Ouvrir la timeline continue en pleine largeur"
              >
                <ArrowsPointingOutIcon className="w-3.5 h-3.5" />
                Vue pleine largeur
              </Link>
              {!draggedItem && !freeDrag?.isActive && (
                <span className="text-xs text-gray-400 flex items-center">
                  <Bars2Icon className="w-3.5 h-3.5 mr-1" />
                  Glissez une intervention vers une equipe ou un horaire pour la planifier
                </span>
              )}
              {freeDrag?.isActive && (
                <div className="flex items-center space-x-2 px-3 py-1 bg-amber-100 border border-amber-300 rounded-lg text-sm">
                  <ArrowsRightLeftIcon className="w-4 h-4 text-amber-600 animate-pulse" />
                  <span className="text-amber-700">
                    <strong>{freeDrag.numeroOrdre}</strong> - Heure: <strong>{freeDrag.originalHeure} → {freeDrag.currentHeure}</strong>
                    {freeDrag.currentEquipeId !== freeDrag.sourceEquipeId && (
                      <>
                        {' '}- Equipe: <strong>{timeline?.equipes.find(e => e.id === freeDrag.sourceEquipeId)?.nom}</strong>
                        {' '}→{' '}
                        <strong>{timeline?.equipes.find(e => e.id === freeDrag.currentEquipeId)?.nom}</strong>
                      </>
                    )}
                  </span>
                </div>
              )}
              {draggedItem && !freeDrag?.isActive && (
                <div className="flex items-center space-x-2 px-3 py-1 bg-primary-100 border border-primary-300 rounded-lg text-sm">
                  <ArrowsRightLeftIcon className="w-4 h-4 text-primary-600 animate-pulse" />
                  <span className="text-primary-700">
                    Deplacez <strong>{draggedItem.parking}</strong> (Z{draggedItem.zone}) vers une equipe compatible
                  </span>
                </div>
              )}
            </div>
            <div className="card-body p-0 overflow-x-auto relative">
              {/* En-tete des heures */}
              <div className="flex border-b bg-gray-50 sticky top-0">
                <div className="w-40 flex-shrink-0 px-4 py-2 font-medium text-gray-700 border-r">
                  Equipe
                </div>
                <div className="flex-1 flex">
                  {plagesHoraires.map((heure) => (
                    <div
                      key={heure}
                      className="flex-1 text-center text-xs text-gray-500 py-2 border-r border-gray-200"
                      style={{ minWidth: '60px' }}
                    >
                      {heure}
                    </div>
                  ))}
                </div>
              </div>

              {/* Ligne "heure actuelle", uniquement si le jour affiche est aujourd'hui */}
              {selectedDate === new Date().toISOString().split('T')[0] && (
                <div
                  className="absolute pointer-events-none"
                  style={{ left: '160px', width: `${plagesHoraires.length * 60}px`, top: 0, bottom: 0 }}
                >
                  <NowLine
                    getPositionPercent={() => {
                      const now = new Date();
                      return getInterventionPositionGlobal(
                        `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
                      );
                    }}
                  />
                </div>
              )}

              {/* Lignes par equipe */}
              {timeline?.equipes.map((equipe) => (
                <TimelineEquipeRow
                  key={equipe.id}
                  equipe={equipe}
                  plagesHoraires={plagesHoraires}
                  onEditAffectation={(aff, equipeActuelle) => {
                    setEditAffectation({
                      interventionId: aff.intervention_id,
                      numeroOrdre: aff.numero_ordre,
                      parking: aff.parking,
                      zone: aff.zone,
                      statut: aff.statut,
                      currentEquipeId: equipeActuelle.id,
                      heurePlanifiee: aff.heure_planifiee || aff.heure_souhaitee,
                      heureSouhaitee: aff.heure_souhaitee,
                    });
                  }}
                  getPosition={getInterventionPositionGlobal}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  isDragTarget={dropTargetEquipe === equipe.id}
                  draggedItem={draggedItem}
                  freeDrag={freeDrag}
                  onFreeDragStart={handleFreeDragStart}
                  justDraggedRef={justDraggedRef}
                />
              ))}

              {timeline?.equipes.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  Aucune equipe active
                </div>
              )}
            </div>
          </div>

          {/* Liste des interventions */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold flex items-center">
                <ClockIcon className="w-5 h-5 mr-2" />
                Interventions du jour ({timeline?.interventions.length || 0})
              </h3>
            </div>
            <div className="card-body p-0">
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>N° Ordre</th>
                      <th>H. Souhaitee</th>
                      <th>H. Planifiee</th>
                      <th>Parking</th>
                      <th>Priorite</th>
                      <th>Statut</th>
                      <th>Progression</th>
                      <th>Equipes</th>
                      <th>Ecart</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {timeline?.interventions.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="text-center py-8 text-gray-500">
                          Aucune intervention pour cette date
                        </td>
                      </tr>
                    ) : (
                      timeline?.interventions.map((intervention) => (
                        <InterventionRow
                          key={intervention.id}
                          intervention={intervention}
                          onReassign={(zone) => {
                            setReassignData({
                              interventionId: intervention.id,
                              zone,
                              equipeId: 0,
                            });
                            setShowReassignModal(true);
                          }}
                          onDragStart={handleDragStart}
                          onDragEnd={handleDragEnd}
                          isBeingDragged={draggedItem?.interventionId === intervention.id}
                        />
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Panel lateral - Conflits (1/5): informatif mais secondaire au quotidien,
            volontairement compact pour laisser la priorite visuelle a la Timeline */}
        <div className="xl:col-span-1 space-y-3">
          {/* Conflits */}
          <div className="card">
            <div className="card-header py-2">
              <h3 className="text-sm font-semibold flex items-center">
                <ExclamationTriangleIcon className="w-4 h-4 mr-1.5 text-amber-500" />
                Conflits ({conflicts?.total || 0})
              </h3>
            </div>
            <div className="card-body p-0 max-h-48 overflow-y-auto">
              {conflicts?.conflits.length === 0 ? (
                <div className="p-3 text-center text-gray-500 text-sm">
                  <CheckCircleIcon className="w-6 h-6 mx-auto mb-1 text-green-500" />
                  Aucun conflit detecte
                </div>
              ) : (
                <div className="divide-y">
                  {conflicts?.conflits.map((conflit, index) => (
                    <ConflitItem key={index} conflit={conflit} />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Suggestions d'optimisation */}
          {optimization && optimization.total > 0 && (
            <div className="card">
              <div className="card-header py-2">
                <h3 className="text-sm font-semibold flex items-center">
                  <BoltIcon className="w-4 h-4 mr-1.5 text-primary-500" />
                  Suggestions ({optimization.total})
                </h3>
              </div>
              <div className="card-body p-0 max-h-36 overflow-y-auto">
                <div className="divide-y">
                  {optimization.suggestions.slice(0, 3).map((suggestion, index) => (
                    <SuggestionItem
                      key={index}
                      suggestion={suggestion}
                      onApply={() => {
                        if (suggestion.type === 'affectation' && suggestion.intervention_id && suggestion.equipe_id && suggestion.zone) {
                          reassignMutation.mutate({
                            intervention_id: suggestion.intervention_id,
                            zone: suggestion.zone,
                            equipe_id: suggestion.equipe_id,
                          });
                        }
                      }}
                    />
                  ))}
                </div>
                {optimization.total > 3 && (
                  <div className="p-2 text-center text-xs text-gray-500">
                    +{optimization.total - 3} autres suggestions
                  </div>
                )}
              </div>
              <div className="p-2 border-t bg-gray-50">
                <p className="text-xs text-gray-600">
                  Gain estime: <span className="font-semibold text-green-600">~{optimization.gain_estime} min</span>
                </p>
              </div>
            </div>
          )}

          {/* Resume des ecarts */}
          {timeline?.statistiques.ecarts && (
            <div className="card">
              <div className="card-header py-2">
                <h3 className="text-sm font-semibold">Ecarts temporels</h3>
              </div>
              <div className="card-body py-2">
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Ecart moyen:</span>
                    <span className={timeline.statistiques.ecarts.ecart_moyen > 15 ? 'text-red-600 font-medium' : 'text-green-600 font-medium'}>
                      {timeline.statistiques.ecarts.ecart_moyen > 0 ? '+' : ''}{timeline.statistiques.ecarts.ecart_moyen} min
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Dans les temps:</span>
                    <span className="text-green-600">{timeline.statistiques.ecarts.dans_les_temps}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">En retard:</span>
                    <span className="text-red-600">{timeline.statistiques.ecarts.en_retard}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">En avance:</span>
                    <span className="text-blue-600">{timeline.statistiques.ecarts.en_avance}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de reassignation */}
      {showReassignModal && reassignData && (
        <ReassignModal
          interventionId={reassignData.interventionId}
          zone={reassignData.zone}
          equipes={timeline?.equipes || []}
          onClose={() => {
            setShowReassignModal(false);
            setReassignData(null);
          }}
          onSubmit={(equipeId) => {
            reassignMutation.mutate({
              intervention_id: reassignData.interventionId,
              zone: reassignData.zone,
              equipe_id: equipeId,
            });
          }}
          isLoading={reassignMutation.isPending}
        />
      )}

      {/* Modal d'edition d'une affectation planifiee (clic sur un bloc de la Timeline) */}
      {editAffectation && (
        <EditAffectationModal
          affectation={editAffectation}
          equipes={timeline?.equipes || []}
          onClose={() => setEditAffectation(null)}
          onChangeEquipe={(equipeId) => {
            reassignMutation.mutate(
              {
                intervention_id: editAffectation.interventionId,
                zone: editAffectation.zone,
                equipe_id: equipeId,
              },
              {
                onSuccess: () => {
                  toast.success(`Equipe modifiee pour ${editAffectation.numeroOrdre}`);
                  setEditAffectation(null);
                },
                onError: (error) => toast.error(getErrorMessage(error)),
              }
            );
          }}
          onChangeHeure={(heure) => {
            updateScheduleMutation.mutate(
              {
                intervention_id: editAffectation.interventionId,
                heure_planifiee: heure,
              },
              { onSuccess: () => setEditAffectation(null) }
            );
          }}
          isSavingEquipe={reassignMutation.isPending}
          isSavingHeure={updateScheduleMutation.isPending}
        />
      )}

      {/* Modal du plan d'optimisation */}
      {showPlanModal && generatedPlan && (
        <OptimizationPlanModal
          plan={generatedPlan}
          onClose={() => {
            setShowPlanModal(false);
            setGeneratedPlan(null);
          }}
          onApply={() => {
            applyPlanMutation.mutate(generatedPlan.plan);
          }}
          isApplying={applyPlanMutation.isPending}
        />
      )}
    </div>
  );
}

// Composant ligne d'equipe dans la timeline
function TimelineEquipeRow({
  equipe,
  plagesHoraires,
  onEditAffectation,
  getPosition,
  onDragOver,
  onDragLeave,
  onDrop,
  isDragTarget,
  draggedItem,
  freeDrag,
  onFreeDragStart,
  justDraggedRef,
}: {
  equipe: TimelineEquipe;
  plagesHoraires: string[];
  onEditAffectation: (aff: TimelineEquipeAffectation, equipe: TimelineEquipe) => void;
  getPosition: (heure: string) => number;
  onDragOver: (equipeId: number) => void;
  onDragLeave: () => void;
  onDrop: (equipeId: number) => void;
  isDragTarget: boolean;
  draggedItem: DragData | null;
  freeDrag: FreeDragData | null;
  onFreeDragStart: (data: DragData, startX: number, heure: string) => void;
  justDraggedRef: { current: boolean };
}) {
  // Verifier si l'equipe peut recevoir l'element en cours de drag
  const canReceiveDrag = useMemo(() => {
    if (!draggedItem) return false;
    if (draggedItem.sourceEquipeId === equipe.id) return false;

    // Zone 1-4 => pony requis, Zone >= 5 => tracteur requis
    const requiresPony = draggedItem.zone <= 4;
    const isPonyEquipe = equipe.type_engin === 'pony';

    return (requiresPony && isPonyEquipe) || (!requiresPony && !isPonyEquipe);
  }, [draggedItem, equipe.id, equipe.type_engin]);

  // Pendant un drag libre, survoler la ligne d'ORIGINE du bloc (deplacement
  // horizontal seul, sans changement d'equipe) ne doit pas s'afficher comme un
  // depot refuse: ce n'est pas une tentative de depot, juste un ajustement d'heure.
  const isSourceRow = draggedItem?.sourceEquipeId === equipe.id;

  return (
    <div
      className={`flex border-b transition-colors ${
        isDragTarget && isSourceRow
          ? ''
          : isDragTarget && canReceiveDrag
          ? 'bg-primary-100 border-primary-300'
          : isDragTarget && !canReceiveDrag
          ? 'bg-red-100 border-red-300'
          : 'hover:bg-gray-50'
      }`}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver(equipe.id);
      }}
      onDragLeave={(e) => {
        // Eviter le declenchement si on passe sur un enfant
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          onDragLeave();
        }
      }}
      onDrop={(e) => {
        e.preventDefault();
        if (canReceiveDrag) {
          onDrop(equipe.id);
        }
      }}
    >
      <div className="w-40 flex-shrink-0 px-4 py-3 border-r">
        <div className="font-medium text-sm">{equipe.nom}</div>
        <div className="text-xs text-gray-500">{equipe.type_engin_label}</div>
        {/* Chef/vehicule - synchronise avec l'ecran Equipes (meme regle :
            chef affiche seulement s'il est au planning, cf signalement
            utilisateur "la partie Equipe n'est pas synchro"). */}
        {equipe.chef && <div className="text-xs text-gray-500 truncate" title={equipe.chef}>{equipe.chef}</div>}
        {equipe.engin && (
          <div
            className={`text-xs truncate ${equipe.engin.disponible ? 'text-gray-500' : 'text-amber-600'}`}
            title={equipe.engin.nom}
          >
            {equipe.engin.nom}
          </div>
        )}
        <div className="text-xs text-gray-400">{equipe.charge} affectation(s)</div>
        {isDragTarget && !isSourceRow && (
          <div className={`text-xs mt-1 font-medium ${canReceiveDrag ? 'text-primary-600' : 'text-red-600'}`}>
            {canReceiveDrag ? 'Deposer ici' : 'Incompatible'}
          </div>
        )}
      </div>
      <div
        data-timeline-container
        data-equipe-id={equipe.id}
        className="flex-1 relative h-20"
        style={{ minWidth: `${plagesHoraires.length * 60}px` }}
      >
        {/* Grille des heures */}
        <div className="absolute inset-0 flex">
          {plagesHoraires.map((_, i) => (
            <div key={i} className="flex-1 border-r border-gray-100" style={{ minWidth: '60px' }} />
          ))}
        </div>

        {/* Affectations */}
        {equipe.affectations.map((aff, index) => {
          // Utiliser l'heure planifiee (ou souhaitee par defaut)
          const heurePlanifiee = aff.heure_planifiee || aff.heure_debut;
          const heureModifiee = aff.heure_planifiee && aff.heure_planifiee !== aff.heure_souhaitee;
          // Utiliser la position en cours de drag si c'est cette intervention
          const isBeingDragged = freeDrag?.interventionId === aff.intervention_id && freeDrag?.isActive;
          const displayHeure = isBeingDragged ? freeDrag.currentHeure : heurePlanifiee;
          const left = getPosition(displayHeure);
          const width = 12; // Largeur fixe (2h = ~12.5%)
          const colorClass = STATUT_TIMELINE_COLORS[aff.statut] || 'bg-gray-200 border-gray-400';
          // Modifiable tant que l'intervention n'est pas terminee/annulee
          const estModifiable = !['cloturee', 'annulee'].includes(aff.statut);

          return (
            <div
              key={index}
              className={`absolute top-4 h-12 rounded border-2 hover:opacity-80 hover:shadow-md select-none ${colorClass} ${
                isBeingDragged ? 'ring-2 ring-primary-400 shadow-lg z-10' : ''
              } ${heureModifiee ? 'ring-1 ring-amber-400' : ''} ${estModifiable ? 'cursor-move' : 'cursor-default'}`}
              style={{
                left: `${left}%`,
                width: `${width}%`,
                minWidth: '60px',
                transition: isBeingDragged ? 'none' : 'left 0.15s ease-out',
              }}
              onClick={(e) => {
                // Ne pas ouvrir l'edition si le pointeur a reellement bouge pendant
                // le geste (ref lue de maniere synchrone, fiable meme si le
                // re-rendu declenche par le mouseup n'est pas encore termine)
                if (!justDraggedRef.current) {
                  onEditAffectation(aff, equipe);
                }
              }}
              onMouseDown={(e) => {
                if (estModifiable && e.button === 0) {
                  e.preventDefault();
                  e.stopPropagation();
                  onFreeDragStart(
                    {
                      interventionId: aff.intervention_id,
                      zone: aff.zone,
                      sourceEquipeId: equipe.id,
                      parking: aff.parking,
                      numeroOrdre: aff.numero_ordre,
                    },
                    e.clientX,
                    heurePlanifiee
                  );
                }
              }}
              title={`${aff.numero_ordre} - ${aff.parking} - Zone ${aff.zone}\nHeure souhaitee: ${aff.heure_souhaitee}\nHeure planifiee: ${displayHeure}${heureModifiee ? ' (modifiee)' : ''}\nMaintenir clic + glisser pour changer l'heure et/ou l'equipe`}
            >
              <div className="px-2 py-1 text-xs truncate pointer-events-none">
                <div className="font-medium">{aff.parking}</div>
                <div className="text-gray-600 flex justify-between">
                  <span>Z{aff.zone}</span>
                  <span className={`font-mono ${heureModifiee ? 'text-amber-600 font-semibold' : ''}`}>
                    {displayHeure}{heureModifiee ? '*' : ''}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Composant ligne d'intervention
function InterventionRow({
  intervention,
  onReassign,
  onDragStart,
  onDragEnd,
  isBeingDragged,
}: {
  intervention: TimelineIntervention;
  onReassign: (zone: number) => void;
  onDragStart?: (data: DragData) => void;
  onDragEnd?: () => void;
  isBeingDragged?: boolean;
}) {
  const queryClient = useQueryClient();
  const ecartJalon = intervention.jalons.find((j) => j.type === 'fin_reelle');

  // Zone a utiliser pour le drag&drop vers la Timeline: en priorite une zone pas
  // encore affectee (c'est le travail de planification), sinon la premiere zone
  // existante (permet aussi de re-affecter directement depuis le tableau).
  const zoneNonAffectee = intervention.equipes.find((e) => !e.id);
  const dragZone = zoneNonAffectee?.zone ?? intervention.equipes[0]?.zone ?? 1;
  const dragSourceEquipeId = zoneNonAffectee ? 0 : (intervention.equipes[0]?.id ?? 0);

  // Mutation pour valider une intervention cloturee
  const validerMutation = useMutation({
    mutationFn: () => deneignementService.valider(intervention.id),
    onSuccess: () => {
      toast.success(`Intervention ${intervention.numero_ordre} validee`);
      queryClient.invalidateQueries({ queryKey: ['regulation-timeline'] });
      queryClient.invalidateQueries({ queryKey: ['regulation-conflicts'] });
    },
    onError: () => {
      toast.error('Erreur lors de la validation');
    },
  });

  // Telecharger le PDF d'une intervention validee
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const token = useAuthStore.getState().token;
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/deneignements/${intervention.id}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Erreur HTTP ' + response.status);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bon_intervention_${intervention.numero_ordre}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      toast.error('Erreur lors du telechargement du PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Verifier si l'heure planifiee est differente de l'heure souhaitee
  const heurePlanifieeModifiee = intervention.heure_planifiee &&
    intervention.heure_planifiee !== intervention.heure_souhaitee;

  return (
    <tr className={`${intervention.en_retard ? 'bg-red-50' : ''} ${isBeingDragged ? 'opacity-40' : ''}`}>
      <td className="font-medium text-primary-600">
        <div className="flex items-center gap-1.5">
          {onDragStart && (
            <span
              draggable
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', String(intervention.id));
                onDragStart({
                  interventionId: intervention.id,
                  zone: dragZone,
                  sourceEquipeId: dragSourceEquipeId,
                  parking: intervention.parking,
                  numeroOrdre: intervention.numero_ordre,
                });
              }}
              onDragEnd={onDragEnd}
              className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500"
              title="Glisser vers une equipe dans la Timeline pour planifier"
            >
              <Bars2Icon className="w-4 h-4" />
            </span>
          )}
          <span>{intervention.numero_ordre}</span>
        </div>
      </td>
      <td className="text-gray-500">{intervention.heure_souhaitee}</td>
      <td className={heurePlanifieeModifiee ? 'font-medium text-amber-600' : ''}>
        {intervention.heure_planifiee || intervention.heure_souhaitee}
        {heurePlanifieeModifiee && (
          <span className="ml-1 text-xs text-amber-500" title="Modifie par le regulateur">*</span>
        )}
      </td>
      <td>{intervention.parking}</td>
      <td>
        <PrioriteBadge priorite={intervention.priorite as any} size="sm" />
      </td>
      <td>
        <StatusBadge statut={intervention.statut as any} size="sm" />
      </td>
      <td>
        <div className="flex items-center space-x-2">
          <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary-500 rounded-full transition-all"
              style={{ width: `${intervention.progression}%` }}
            />
          </div>
          <span className="text-xs text-gray-500">
            {intervention.jalons_attendus != null
              ? `${intervention.jalons_termines} / ${intervention.jalons_attendus} jalons`
              : `${intervention.progression}%`}
          </span>
        </div>
      </td>
      <td>
        <div className="flex flex-wrap gap-1">
          {intervention.equipes.map((eq, i) => (
            <span key={i} className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">
              {eq.nom || 'Non affecte'} (Z{eq.zone})
            </span>
          ))}
        </div>
      </td>
      <td>
        {ecartJalon?.ecart_minutes !== null && ecartJalon?.ecart_minutes !== undefined ? (
          <span className={ecartJalon.ecart_minutes > 15 ? 'text-red-600 font-medium' : ecartJalon.ecart_minutes < 0 ? 'text-blue-600' : 'text-green-600'}>
            {ecartJalon.ecart_minutes > 0 ? '+' : ''}{ecartJalon.ecart_minutes} min
          </span>
        ) : (
          <span className="text-gray-400">-</span>
        )}
      </td>
      <td>
        <div className="flex items-center space-x-1">
          <Link
            to={`/deneignements/${intervention.id}`}
            className="p-1 text-gray-400 hover:text-primary-500"
            title="Voir"
          >
            <EyeIcon className="w-4 h-4" />
          </Link>
          <button
            onClick={() => {
              // Trouver la premiere zone non affectee
              const zoneNonAffectee = intervention.equipes.find((e) => !e.id);
              onReassign(zoneNonAffectee?.zone || 1);
            }}
            className="p-1 text-gray-400 hover:text-primary-500"
            title="Reassigner"
          >
            <ArrowsRightLeftIcon className="w-4 h-4" />
          </button>
          {intervention.statut === 'cloturee' && (
            <button
              onClick={() => validerMutation.mutate()}
              disabled={validerMutation.isPending}
              className="p-1 text-green-500 hover:text-green-700"
              title="Valider l'intervention"
            >
              <CheckIcon className="w-4 h-4" />
            </button>
          )}
          {intervention.statut === 'validee' && (
            <button
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              className={`p-1 ${downloadingPdf ? 'text-gray-400' : 'text-blue-500 hover:text-blue-700'}`}
              title="Telecharger le PDF"
            >
              <DocumentArrowDownIcon className={`w-4 h-4 ${downloadingPdf ? 'animate-pulse' : ''}`} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// Composant conflit
function ConflitItem({ conflit }: { conflit: Conflit }) {
  const Icon = CONFLIT_ICONS[conflit.niveau];
  const colorClass = CONFLIT_COLORS[conflit.niveau];

  return (
    <div className={`p-3 border-l-4 ${colorClass}`}>
      <div className="flex items-start space-x-2">
        <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm">{conflit.message}</p>
          {conflit.intervention_id && (
            <Link
              to={`/deneignements/${conflit.intervention_id}`}
              className="text-xs text-primary-600 hover:underline"
            >
              Voir l'intervention
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

// Composant suggestion
function SuggestionItem({
  suggestion,
  onApply,
}: {
  suggestion: Suggestion;
  onApply: () => void;
}) {
  return (
    <div className="p-3 hover:bg-gray-50">
      <div className="flex items-start justify-between space-x-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-700">{suggestion.raison}</p>
        </div>
        {suggestion.type === 'affectation' && (
          <button
            onClick={onApply}
            className="px-2 py-1 text-xs bg-primary-500 text-white rounded hover:bg-primary-600"
          >
            Appliquer
          </button>
        )}
      </div>
    </div>
  );
}

// Modal de reassignation
function ReassignModal({
  interventionId,
  zone,
  equipes,
  onClose,
  onSubmit,
  isLoading,
}: {
  interventionId: number;
  zone: number;
  equipes: TimelineEquipe[];
  onClose: () => void;
  onSubmit: (equipeId: number) => void;
  isLoading: boolean;
}) {
  const [selectedEquipe, setSelectedEquipe] = useState<number>(0);

  // Filtrer les equipes compatibles avec la zone
  const equipesCompatibles = equipes.filter((e) => {
    if (zone <= 4) return e.type_engin === 'pony';
    return e.type_engin === 'tracteur';
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-semibold">Reassigner la zone {zone}</h3>
          <p className="text-sm text-gray-500">Intervention #{interventionId}</p>
        </div>
        <div className="px-6 py-4">
          <label className="label">Equipe</label>
          <select
            className="input"
            value={selectedEquipe}
            onChange={(e) => setSelectedEquipe(Number(e.target.value))}
          >
            <option value={0}>Selectionnez une equipe</option>
            {equipesCompatibles.map((equipe) => (
              <option key={equipe.id} value={equipe.id}>
                {equipe.nom} ({equipe.type_engin_label}) - {equipe.charge} affectation(s)
              </option>
            ))}
          </select>
          {equipesCompatibles.length === 0 && (
            <p className="mt-2 text-sm text-amber-600">
              Aucune equipe compatible avec la zone {zone} ({zone <= 4 ? 'Pony requis' : 'Tracteur requis'})
            </p>
          )}
        </div>
        <div className="px-6 py-4 border-t flex justify-end space-x-3">
          <button onClick={onClose} className="btn-secondary">
            Annuler
          </button>
          <button
            onClick={() => onSubmit(selectedEquipe)}
            disabled={!selectedEquipe || isLoading}
            className="btn-primary"
          >
            {isLoading ? 'Reassignation...' : 'Reassigner'}
          </button>
        </div>
      </div>
    </div>
  );
}


// Modal du plan d'optimisation
function OptimizationPlanModal({
  plan,
  onClose,
  onApply,
  isApplying,
}: {
  plan: GeneratePlanResponse;
  onClose: () => void;
  onApply: () => void;
  isApplying: boolean;
}) {
  const PRIORITE_COLORS: Record<string, string> = {
    critique: 'bg-red-100 text-red-800',
    urgente: 'bg-orange-100 text-orange-800',
    normale: 'bg-gray-100 text-gray-800',
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Plan d'optimisation</h3>
            <p className="text-sm text-gray-500">
              {plan.date} - Score global: <span className="font-medium text-primary-600">{plan.score_global}/100</span>
            </p>
          </div>
          <div className="flex items-center space-x-4 text-sm">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary-600">{plan.resume.total_affectations}</div>
              <div className="text-gray-500">Affectations</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{plan.resume.taux_couverture}%</div>
              <div className="text-gray-500">Couverture</div>
            </div>
          </div>
        </div>

        {/* Statistiques */}
        <div className="px-6 py-3 bg-gray-50 border-b grid grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Charge moyenne:</span>
            <span className="ml-2 font-medium">{plan.statistiques.charge_moyenne}</span>
          </div>
          <div>
            <span className="text-gray-500">Ecart charge:</span>
            <span className={`ml-2 font-medium ${plan.statistiques.ecart_charge <= 1 ? 'text-green-600' : 'text-amber-600'}`}>
              {plan.statistiques.ecart_charge}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Critiques:</span>
            <span className="ml-2 font-medium text-red-600">{plan.statistiques.par_priorite.critique}</span>
          </div>
          <div>
            <span className="text-gray-500">Urgentes:</span>
            <span className="ml-2 font-medium text-orange-600">{plan.statistiques.par_priorite.urgente}</span>
          </div>
        </div>

        {/* Liste des affectations */}
        <div className="flex-1 overflow-y-auto p-6">
          {plan.plan.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CheckCircleIcon className="w-12 h-12 mx-auto mb-3 text-green-500" />
              <p>Toutes les interventions sont deja affectees</p>
            </div>
          ) : (
            <div className="space-y-2">
              <h4 className="font-medium text-gray-700 mb-3">Affectations proposees ({plan.plan.length})</h4>
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-left">Intervention</th>
                    <th className="px-3 py-2 text-left">Parking</th>
                    <th className="px-3 py-2 text-left">Heure</th>
                    <th className="px-3 py-2 text-left">Zone</th>
                    <th className="px-3 py-2 text-left">Equipe</th>
                    <th className="px-3 py-2 text-left">Priorite</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {plan.plan.map((aff, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium text-primary-600">{aff.intervention_numero}</td>
                      <td className="px-3 py-2">{aff.parking || '-'}</td>
                      <td className="px-3 py-2">{aff.heure || '-'}</td>
                      <td className="px-3 py-2">Zone {aff.zone}</td>
                      <td className="px-3 py-2 font-medium">{aff.equipe_nom}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded text-xs ${PRIORITE_COLORS[aff.priorite || 'normale']}`}>
                          {aff.priorite || 'normale'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Non affectables */}
          {plan.non_affectables.length > 0 && (
            <div className="mt-6">
              <h4 className="font-medium text-amber-700 mb-3 flex items-center">
                <ExclamationTriangleIcon className="w-5 h-5 mr-2" />
                Zones non affectables ({plan.non_affectables.length})
              </h4>
              <div className="space-y-2">
                {plan.non_affectables.map((item, index) => (
                  <div key={index} className="p-3 bg-amber-50 border border-amber-200 rounded text-sm">
                    <span className="font-medium">{item.intervention_numero}</span>
                    <span className="mx-2">-</span>
                    <span>Zone {item.zone}</span>
                    <span className="mx-2">:</span>
                    <span className="text-amber-700">{item.raison}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Charge par equipe */}
          {Object.keys(plan.statistiques.charge_par_equipe).length > 0 && (
            <div className="mt-6">
              <h4 className="font-medium text-gray-700 mb-3">Repartition de charge par equipe</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(plan.statistiques.charge_par_equipe).map(([equipe, charge]) => (
                  <div key={equipe} className="p-3 bg-gray-50 rounded border">
                    <div className="text-sm text-gray-500 truncate">{equipe}</div>
                    <div className="text-xl font-bold">{charge as number}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t flex justify-between items-center">
          <p className="text-sm text-gray-500">
            {plan.plan.length > 0
              ? `${plan.plan.length} affectation(s) seront appliquees`
              : 'Aucune affectation a appliquer'}
          </p>
          <div className="flex space-x-3">
            <button onClick={onClose} className="btn-secondary">
              Fermer
            </button>
            {plan.plan.length > 0 && (
              <button
                onClick={onApply}
                disabled={isApplying}
                className="btn-primary"
              >
                {isApplying ? 'Application...' : 'Appliquer le plan'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default RegulationPage;
